<template>
    <BaseModal :title="learnerId ? 'Edit Learner' : 'Add Learner'" @close="closeModal" :visible="true">
        <form @submit.prevent="onSubmit">
            <div class="rlms-flex rlms-flex-col rlms-gap-10">
                <div class="rlms-col-1">
                    <p class="rlms-fw-500">Learner Information</p>
                </div>
                <div class="pp-body-scroll">
                    <div class="rlms-row rlms-row-responsive">
                        <div class="rlms-col-1">
                            <input type="text" placeholder="A code for the learner(optional)"
                                v-model.trim="information_code" />
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && information_codeError">{{ information_codeError }}</span>
                        </div>
                        <div class="rlms-col-2">
                            <input type="text" placeholder="First Name" v-model.trim="firstname" />
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && firstnameError">{{ firstnameError }}</span>
                        </div>
                        <div class="rlms-col-2">
                            <input type="text" placeholder="Last Name" v-model.trim="lastname" />
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && lastnameError">{{ lastnameError }}</span>
                        </div>
                        <div class="rlms-col-1">
                            <div class="input-icon-group input-icon-right">
                                <input type="date" v-model="dob" :max="new Date().toISOString().split('T')[0]"
                                    placeholder="Date of Birth" />
                                <svg class="rlms-input-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                    viewBox="0 0 20 20" fill="none" style="z-index: -1;">
                                    <path d="M6.66667 1.66699V4.16699" stroke="#8A8893" stroke-width="1.5"
                                        stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M13.3333 1.66699V4.16699" stroke="#8A8893" stroke-width="1.5"
                                        stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M2.91667 7.5752H17.0833" stroke="#8A8893" stroke-width="1.5"
                                        stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                    <path
                                        d="M17.5 7.08366V14.167C17.5 16.667 16.25 18.3337 13.3333 18.3337H6.66667C3.75 18.3337 2.5 16.667 2.5 14.167V7.08366C2.5 4.58366 3.75 2.91699 6.66667 2.91699H13.3333C16.25 2.91699 17.5 4.58366 17.5 7.08366Z"
                                        stroke="#8A8893" stroke-width="1.5" stroke-miterlimit="10"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M13.0791 11.4167H13.0866" stroke="#8A8893" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M13.0791 13.9167H13.0866" stroke="#8A8893" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M9.99607 11.4167H10.0036" stroke="#8A8893" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M9.99607 13.9167H10.0036" stroke="#8A8893" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M6.91209 11.4167H6.91957" stroke="#8A8893" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M6.91209 13.9167H6.91957" stroke="#8A8893" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </div>
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && dobError">{{
                                dobError }}</span>
                        </div>
                        <div class="rlms-col-2">
                            <div class="input-icon-group input-icon-right">
                                <select v-model="gender">
                                    <option value="">Select Gender</option>
                                    <option value="1">Male</option>
                                    <option value="2">Female</option>
                                    <option value="3">Other</option>
                                </select>
                                <svg class="rlms-input-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="8"
                                    viewBox="0 0 14 8" fill="none">
                                    <path d="M1 1L7 7L13 1" stroke="#8A8893" stroke-width="1.5" stroke-linecap="round"
                                        stroke-linejoin="round" />
                                </svg>
                            </div>
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && genderError">{{
                                genderError }}</span>
                        </div>
                        <div class="rlms-col-2">
                            <div class="input-icon-group input-icon-right">
                                <select v-model="language_id">
                                    <option value="">Select Language</option>
                                    <option v-for="language in languages" :key="language.id" :value="language.id">
                                        {{ capitalize(language.name) }}
                                    </option>
                                </select>
                                <svg class="rlms-input-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="8"
                                    viewBox="0 0 14 8" fill="none">
                                    <path d="M1 1L7 7L13 1" stroke="#8A8893" stroke-width="1.5" stroke-linecap="round"
                                        stroke-linejoin="round" />
                                </svg>
                            </div>
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && language_idError">{{ language_idError }}</span>
                        </div>
                        <div class="rlms-col-2">
                            <input type="text" placeholder="The learner's parents(s) (optional)" v-model="parents" />
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && parentsError">{{ parentsError }}</span>
                        </div>
                    </div>
                </div>
                <div class="rlms-row">
                    <div class="rlms-col">
                        <div class="rlms-fw-500 rlms-mb-1">Select Status</div>
                        <div class="rlms-flex rlms-items-center rlms-gap-10">
                            <label class="rlms-us-none rlms-flex rlms-items-center rlms-gap-10">
                                <input type="radio" v-model="status" value="1" />
                                <span>Active</span>
                            </label>
                            <label class="rlms-us-none rlms-flex rlms-items-center rlms-gap-10">
                                <input type="radio" v-model="status" value="0" />
                                <span>Inactive</span>
                            </label>
                        </div>
                        <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && statusError">{{
                            statusError }}</span>
                    </div>
                </div>
            </div>
        </form>
        <template #footer>
            <div class="pp-footer rlms-flex rlms-justify-between" style="padding: 0;">
                <button class="rlms-btn rlms-btn-outline-primary p-cancel-btn" type="button" id="cancel-org-modal"
                    @click="closeModal">Cancel</button>
                <button type="submit" class="rlms-btn rlms-btn-primary" id="save-org-btn"
                    @click="onSubmit">Save</button>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { capitalize, onMounted, ref, watch } from 'vue';
import BaseModal from './BaseModal.vue';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';
import axios from '@/services/axios';
import { showToast } from '@/utils/swal';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';

const loader = useLoaderStore();

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);

// eslint-disable-next-line
const props = defineProps({
    organizationId: {
        type: Number,
        required: true,
    },
    learner: {
        type: Object,
        default: null
    }
});

if (!props.organizationId || typeof props.organizationId !== 'number') {
    throw new Error('orgId prop is required');
}

const learnerId = ref(null);
const showModal = ref(true);
const submitting = ref(false);
const submitted = ref(false);
const languages = ref([]);

const closeModal = () => {
    showModal.value = false;
    emit('close');
};

// Replace this with props or API fetched user
const formSchema = yup.object({
    information_code: yup.string().optional(),
    firstname: yup.string().required('The first name is required.'),
    lastname: yup.string().required('The last name is required.'),
    dob: yup.string().required('Choose a valid date of birth.'),
    gender: yup.string().required('Please select a gender.'),
    language_id: yup.string().required('Please select a language.'),
    parents: yup.string().optional(),
    status: yup.boolean().required('Please choose a learner status.'),
});

// Setup form with schema
const { handleSubmit, setErrors, setFieldValue } = useForm({
    validationSchema: formSchema,
    initialValues: {
        information_code: '',
        firstname: '',
        lastname: '',
        dob: '',
        gender: '',
        language_id: '',
        parents: '',
        status: 1
    }
})

// Fields
const { value: information_code, errorMessage: information_codeError } = useField('information_code');
const { value: firstname, errorMessage: firstnameError } = useField('firstname');
const { value: lastname, errorMessage: lastnameError } = useField('lastname');
const { value: dob, errorMessage: dobError } = useField('dob');
const { value: gender, errorMessage: genderError } = useField('gender');
const { value: language_id, errorMessage: language_idError } = useField('language_id');
const { value: parents, errorMessage: parentsError } = useField('parents');
const { value: status, errorMessage: statusError } = useField('status');

// Submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async (values) => {
        submitting.value = true;

        try {
            loader.show();
            if (learnerId.value) {
                await axios.put(`/organizations/${props.organizationId}/learners/${learnerId.value}`, { ...values });
            } else {
                await axios.post(`/organizations/${props.organizationId}/learners`, { ...values });
            }

            showToast({
                icon: 'success',
                title: 'Great! ',
                text: (learnerId.value) ? 'Learner updated successfully!' : 'Learner created successfully!',
            })

            emit('saved');
        } catch (error) {
            if (error?.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach(key => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    handleError(error, true);
                }
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};

// Watch for prop change and populate form
watch(
    () => props.learner,
    (learner) => {
        if (learner) {
            learnerId.value = learner.id || null;
            setFieldValue('information_code', learner.information_code || '');
            setFieldValue('firstname', learner.firstname || '');
            setFieldValue('lastname', learner.lastname || '');
            setFieldValue('dob', learner.dob || '');
            setFieldValue('gender', learner.gender || '');
            setFieldValue('language_id', learner.language_id || '');
            setFieldValue('parents', learner.parents || '');
            setFieldValue('status', learner.setup ? 1 : 0);
        }
    },
    { immediate: true }
);


onMounted(() => {
    fetchLanguages();
})

const fetchLanguages = async () => {
    try {
        const { data } = await axios.get('/languages');
        languages.value = data.data;
    } catch (error) {
        handleError(error);
    }
}
</script>
